import {DataTableColumn} from "mantine-datatable";
import {Button, Group} from '@mantine/core';
import {useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {BillableEvent} from "../types.ts";
import {I18n} from "@aws-amplify/core";
import {StatusBadge} from "../profile/StatusBadge.tsx";
import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";
import {showNotification} from '@mantine/notifications';
import {isAlreadyWrittenOff, requeue, writeOff} from '../services/BillableEventsService.ts';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications.tsx';
import {WriteOffButton} from '../buttons/WriteOffButton.tsx';

export const useTableColumns = () => {
    const queryClient = useQueryClient();
    // Track in-flight requeues so the button disables per-row (the requeue is async; a
    // double-click would emit a second bus event before the row leaves FAILED).
    const [requeuing, setRequeuing] = useState<Record<string, boolean>>({});

    // FNP-663 (FNP-644 part 2): request a requeue of a FAILED BillableEvent. Emits a bus
    // event the aggregator consumes to re-resolve the corrected assignment, rebuild the
    // payload, rotate the key, and flip the row FAILED -> PENDING. Async — we refresh the
    // list so the new state shows once the orchestration lands.
    const handleRequeue = async (id: string) => {
        setRequeuing(prev => ({...prev, [id]: true}));
        try {
            await requeue(id);
            notifySuccess(I18n.get('Requeue requested — the row will move to PENDING shortly'));
            queryClient.invalidateQueries({queryKey: ['billableEvents']});
        } catch (e) {
            notifyError(e);
        } finally {
            setRequeuing(prev => ({...prev, [id]: false}));
        }
    };

    // FNP-1714: write off a FAILED BillableEvent. Synchronous, so the row leaves FAILED as
    // soon as the list refreshes. "Already written off" is its own notification: nothing
    // failed, the row was simply decided before (another admin, or a stale list). Any other
    // failure re-throws after notifying, so the dialog stays open with the reason intact.
    const handleWriteOff = async (id: string, reason: string) => {
        try {
            await writeOff(id, reason);
            notifySuccess(I18n.get('Written off. The event will never be billed.'));
        } catch (e) {
            if (isAlreadyWrittenOff(e)) {
                showNotification({
                    title: I18n.get('Already written off'),
                    message: I18n.get('This event was already written off. Nothing changed.'),
                    color: 'yellow',
                });
            } else {
                notifyError(e);
                throw e;
            }
        } finally {
            queryClient.invalidateQueries({queryKey: ['billableEvents']});
        }
    };

    const columns: DataTableColumn<BillableEvent>[] = [
        {
            accessor: 'customerId',
            title: I18n.get('Customer'),
            width: '1fr',
            render: (item: BillableEvent) => item.customerId,
        },
        {
            accessor: 'channel',
            title: I18n.get('Channel'),
            width: '1fr',
            render: (item: BillableEvent) => item.channel ?? '-',
        },
        {
            accessor: 'state',
            title: I18n.get('State'),
            width: '1fr',
            render: (item: BillableEvent) => <StatusBadge status={item.state}/>,
        },
        {
            accessor: 'meteringRecordId',
            title: I18n.get('Metering Record'),
            width: '1fr',
            render: (item: BillableEvent) => item.meteringRecordId,
        },
        {
            accessor: 'emittedAt',
            title: I18n.get('Emitted At'),
            width: '1fr',
            render: (item: BillableEvent) => item.emittedAt ? formatTimestamp(item.emittedAt) : '-',
        },
        {
            accessor: 'lastAttemptAt',
            title: I18n.get('Last Attempt'),
            width: '1fr',
            render: (item: BillableEvent) => item.lastAttemptAt ? formatTimestamp(item.lastAttemptAt) : '-',
        },
        {
            accessor: 'actions',
            title: I18n.get('Actions'),
            width: '1fr',
            // FNP-663 (FNP-644 part 2): a Requeue button on FAILED rows only (no-invisible-
            // processes — the admin requeue is a visible action). Requeue rotates the
            // idempotency key + rebuilds the payload from the CORRECTED assignment, so it's
            // the deliberate "I've fixed the binding, retry this" action. Only FAILED is
            // requeable (the backend mutation conditions on state == FAILED too).
            render: (item: BillableEvent) => (
                <Group gap="xs">
                    {item.state === 'FAILED' && (
                        <Button
                            size="xs"
                            loading={!!requeuing[item.id]}
                            onClick={() => handleRequeue(item.id)}
                        >
                            {I18n.get('Requeue')}
                        </Button>
                    )}
                    {/* FNP-1714: FAILED only, like Requeue. The UI hides it elsewhere; billing-api
                        is the authority (admin-only, conditional on FAILED). */}
                    {item.state === 'FAILED' && (
                        <WriteOffButton onConfirm={(reason) => handleWriteOff(item.id, reason)}/>
                    )}
                </Group>
            ),
        },
    ];

    return {
        columns,
    }
}
